/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace weibull
*/
var weibull = {};

/**
* @name cdf
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/cdf}
*/
import cdf from './../../../../base/dists/weibull/cdf';
setReadOnly( weibull, 'cdf', cdf );

/**
* @name Weibull
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/ctor}
*/
import Weibull from './../../../../base/dists/weibull/ctor';
setReadOnly( weibull, 'Weibull', Weibull );

/**
* @name entropy
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/entropy}
*/
import entropy from './../../../../base/dists/weibull/entropy';
setReadOnly( weibull, 'entropy', entropy );

/**
* @name kurtosis
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/kurtosis}
*/
import kurtosis from './../../../../base/dists/weibull/kurtosis';
setReadOnly( weibull, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/logcdf}
*/
import logcdf from './../../../../base/dists/weibull/logcdf';
setReadOnly( weibull, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/logpdf}
*/
import logpdf from './../../../../base/dists/weibull/logpdf';
setReadOnly( weibull, 'logpdf', logpdf );

/**
* @name mean
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/mean}
*/
import mean from './../../../../base/dists/weibull/mean';
setReadOnly( weibull, 'mean', mean );

/**
* @name median
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/median}
*/
import median from './../../../../base/dists/weibull/median';
setReadOnly( weibull, 'median', median );

/**
* @name mgf
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/mgf}
*/
import mgf from './../../../../base/dists/weibull/mgf';
setReadOnly( weibull, 'mgf', mgf );

/**
* @name mode
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/mode}
*/
import mode from './../../../../base/dists/weibull/mode';
setReadOnly( weibull, 'mode', mode );

/**
* @name pdf
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/pdf}
*/
import pdf from './../../../../base/dists/weibull/pdf';
setReadOnly( weibull, 'pdf', pdf );

/**
* @name quantile
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/quantile}
*/
import quantile from './../../../../base/dists/weibull/quantile';
setReadOnly( weibull, 'quantile', quantile );

/**
* @name skewness
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/skewness}
*/
import skewness from './../../../../base/dists/weibull/skewness';
setReadOnly( weibull, 'skewness', skewness );

/**
* @name stdev
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/stdev}
*/
import stdev from './../../../../base/dists/weibull/stdev';
setReadOnly( weibull, 'stdev', stdev );

/**
* @name variance
* @memberof weibull
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/weibull/variance}
*/
import variance from './../../../../base/dists/weibull/variance';
setReadOnly( weibull, 'variance', variance );


// EXPORTS //

export default weibull;
